import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  doc,
  documentId,
  getDoc,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
import { useUser } from '../context/UserContext';
import NewsCard from '../components/NewsCard';
import {
  getCurrentNewsCycleKey,
  pinNewsArticle,
  saveNewsArticle,
  unpinNewsArticle,
  unsaveNewsArticle,
} from '../firebase/newsHelpers';
import { getStatesWithHomeFirst } from '../utils/states';
import './NewsPage.css';

const MAX_VISIBLE_ARTICLES = 60;
const INITIAL_VISIBLE_COUNT = 20;
const LOAD_MORE_COUNT = 10;
const FEED_CACHE_PREFIX = 'bluewire.newsFeedCache';

const FEEDS = {
  national: {
    feedDoc: 'national',
    articleCollection: 'newsArticles',
    statsCollection: 'newsArticleStats',
  },
  local_NC: {
    feedDoc: 'local_NC',
    articleCollection: 'localNewsArticles',
    statsCollection: 'localNewsArticleStats',
  },
};

const LOCAL_FEEDS_BY_STATE = {
  'North Carolina': 'local_NC',
  NC: 'local_NC',
};

const normalizeStateName = (value) => {
  const state = String(value || '').trim();
  if (state.toLowerCase() === 'nc') return 'North Carolina';
  return state;
};

const getLocalFeedKeyForState = (value) =>
  LOCAL_FEEDS_BY_STATE[normalizeStateName(value)] || '';

const chunkArray = (items, size) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const toCacheValue = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  return value;
};

const normalizeArticleDoc = (docSnap, articleCollection) => {
  const data = docSnap.data() || {};
  return {
    ...data,
    id: docSnap.id,
    articleCollection,
    publishedAt: toCacheValue(data.publishedAt),
    feedRunAt: toCacheValue(data.feedRunAt),
    fetchedAt: toCacheValue(data.fetchedAt),
    createdAt: toCacheValue(data.createdAt),
    updatedAt: toCacheValue(data.updatedAt),
    adminPinnedAt: toCacheValue(data.adminPinnedAt),
  };
};

const getCacheKey = (feedKey) => `${FEED_CACHE_PREFIX}.${feedKey}`;

const readFeedCache = (feedKey) => {
  try {
    const raw = window.localStorage.getItem(getCacheKey(feedKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (error) {
    return null;
  }
};

const writeFeedCache = (feedKey, payload) => {
  try {
    window.localStorage.setItem(getCacheKey(feedKey), JSON.stringify(payload));
  } catch (error) {
    // Cache failures should never block the feed.
  }
};

const getNcLocalEligible = (profile) => {
  const state = String(profile?.state || '').trim().toLowerCase();
  return state === 'nc' || state === 'north carolina';
};

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sortByPinnedArticles = (articles, pinnedMap) =>
  [...articles].sort((a, b) => {
    const adminPriorityDelta =
      Number(b.adminPinPriority || 0) - Number(a.adminPinPriority || 0);
    if (adminPriorityDelta !== 0) return adminPriorityDelta;

    const adminPinnedAtDelta = toMillis(b.adminPinnedAt) - toMillis(a.adminPinnedAt);
    if (adminPinnedAtDelta !== 0) return adminPinnedAtDelta;

    const aPinnedAt = pinnedMap.get(a.id) || 0;
    const bPinnedAt = pinnedMap.get(b.id) || 0;
    if (aPinnedAt !== bPinnedAt) return bPinnedAt - aPinnedAt;

    return 0;
  });

const mergeStats = (articles, statsById) =>
  articles.map((article) => {
    const stats = statsById.get(article.id);
    if (!stats) return article;
    return {
      ...article,
      commentCount: Number(stats.commentCount || 0),
      adminPinned: stats.adminPinned === true,
      adminPinPriority: Number(stats.adminPinPriority || 0),
      adminPinnedAt: stats.adminPinnedAt || article.adminPinnedAt || null,
      lastCommentAt: stats.lastCommentAt || article.lastCommentAt || null,
      isHidden: stats.isHidden === true,
    };
  }).filter((article) => article.isHidden !== true);

export default function NewsPage() {
  const { currentUser } = useUser();
  const [activeTab, setActiveTab] = useState('national');
  const [cycleKey, setCycleKey] = useState(() => getCurrentNewsCycleKey());
  const [adminLocalState, setAdminLocalState] = useState('');
  const [feedArticles, setFeedArticles] = useState({
    national: [],
    local_NC: [],
  });
  const [savedArticles, setSavedArticles] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [pinnedMap, setPinnedMap] = useState(new Map());
  const [statsById, setStatsById] = useState(new Map());
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [isAdmin, setIsAdmin] = useState(false);
  const loadMoreRef = useRef(null);
  const isNcLocalEligible = getNcLocalEligible(currentUser);
  const profileState = normalizeStateName(currentUser?.state);
  const selectedLocalState = isAdmin
    ? adminLocalState || profileState || 'North Carolina'
    : profileState;
  const activeLocalFeedKey = getLocalFeedKeyForState(selectedLocalState);
  const canViewSelectedLocalFeed = Boolean(activeLocalFeedKey);
  const shouldLoadNcLocalFeed = isAdmin
    ? activeLocalFeedKey === 'local_NC'
    : isNcLocalEligible;
  const localStateOptions = getStatesWithHomeFirst(profileState || 'North Carolina');

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCycleKey(getCurrentNewsCycleKey());
    }, 60 * 1000);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }, [activeTab]);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;
    let cancelled = false;

    const loadFeedFromIndex = async (feedKey, feedData) => {
      const config = FEEDS[feedKey];
      const articleIds = Array.isArray(feedData.articleIds)
        ? feedData.articleIds.slice(0, MAX_VISIBLE_ARTICLES)
        : [];
      const version = String(feedData.version || '');
      const cache = readFeedCache(feedKey);
      const cachedArticlesById =
        cache?.articlesById && typeof cache.articlesById === 'object'
          ? cache.articlesById
          : {};

      if (cache?.version === version) {
        if (cancelled) return;
        setFeedArticles((current) => ({
          ...current,
          [feedKey]: articleIds
            .map((articleId) => cachedArticlesById[articleId])
            .filter(Boolean),
        }));
        return;
      }

      const missingIds = articleIds.filter((articleId) => !cachedArticlesById[articleId]);
      const fetchedArticles = {};
      const chunks = chunkArray(missingIds, 30);

      for (const chunk of chunks) {
        if (chunk.length === 0) continue;
        const snaps = await Promise.all(
          chunk.map((articleId) => getDoc(doc(db, config.articleCollection, articleId)))
        );
        snaps.forEach((docSnap) => {
          if (!docSnap.exists()) return;
          fetchedArticles[docSnap.id] = normalizeArticleDoc(
            docSnap,
            config.articleCollection
          );
        });
      }

      const nextArticlesById = {
        ...cachedArticlesById,
        ...fetchedArticles,
      };
      const orderedArticles = articleIds
        .map((articleId) => nextArticlesById[articleId])
        .filter(Boolean);

      if (cancelled) return;
      writeFeedCache(feedKey, {
        version,
        articleIds,
        articlesById: orderedArticles.reduce((acc, article) => {
          acc[article.id] = article;
          return acc;
        }, {}),
      });

      setFeedArticles((current) => ({
        ...current,
        [feedKey]: orderedArticles,
      }));
    };

    const nationalCache = readFeedCache('national');
    if (nationalCache?.articleIds?.length) {
      setFeedArticles((current) => ({
        ...current,
        national: nationalCache.articleIds
          .map((articleId) => nationalCache.articlesById?.[articleId])
          .filter(Boolean),
      }));
    }

    const unsubscribe = onSnapshot(doc(db, 'newsFeeds', 'national'), (snap) => {
      if (!snap.exists()) {
        setFeedArticles((current) => ({ ...current, national: [] }));
        return;
      }
      loadFeedFromIndex('national', snap.data());
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid || !shouldLoadNcLocalFeed) {
      setFeedArticles((current) => ({ ...current, local_NC: [] }));
      return undefined;
    }
    let cancelled = false;

    const localCache = readFeedCache('local_NC');
    if (localCache?.articleIds?.length) {
      setFeedArticles((current) => ({
        ...current,
        local_NC: localCache.articleIds
          .map((articleId) => localCache.articlesById?.[articleId])
          .filter(Boolean),
      }));
    }

    const loadLocalFeed = async (feedData) => {
      const articleIds = Array.isArray(feedData.articleIds)
        ? feedData.articleIds.slice(0, MAX_VISIBLE_ARTICLES)
        : [];
      const version = String(feedData.version || '');
      const cache = readFeedCache('local_NC');
      const cachedArticlesById =
        cache?.articlesById && typeof cache.articlesById === 'object'
          ? cache.articlesById
          : {};

      if (cache?.version === version) {
        if (cancelled) return;
        setFeedArticles((current) => ({
          ...current,
          local_NC: articleIds
            .map((articleId) => cachedArticlesById[articleId])
            .filter(Boolean),
        }));
        return;
      }

      const missingIds = articleIds.filter((articleId) => !cachedArticlesById[articleId]);
      const fetchedArticles = {};
      for (const chunk of chunkArray(missingIds, 30)) {
        if (chunk.length === 0) continue;
        const snaps = await Promise.all(
          chunk.map((articleId) =>
            getDoc(doc(db, FEEDS.local_NC.articleCollection, articleId))
          )
        );
        snaps.forEach((docSnap) => {
          if (!docSnap.exists()) return;
          fetchedArticles[docSnap.id] = normalizeArticleDoc(
            docSnap,
            FEEDS.local_NC.articleCollection
          );
        });
      }

      const nextArticlesById = {
        ...cachedArticlesById,
        ...fetchedArticles,
      };
      const orderedArticles = articleIds
        .map((articleId) => nextArticlesById[articleId])
        .filter(Boolean);

      if (cancelled) return;
      writeFeedCache('local_NC', {
        version,
        articleIds,
        articlesById: orderedArticles.reduce((acc, article) => {
          acc[article.id] = article;
          return acc;
        }, {}),
      });

      setFeedArticles((current) => ({
        ...current,
        local_NC: orderedArticles,
      }));
    };

    const unsubscribe = onSnapshot(doc(db, 'newsFeeds', 'local_NC'), (snap) => {
      if (!snap.exists()) {
        setFeedArticles((current) => ({ ...current, local_NC: [] }));
        return;
      }
      loadLocalFeed(snap.data());
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [currentUser?.uid, shouldLoadNcLocalFeed]);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;

    const unsubscribe = onSnapshot(
      collection(db, 'users', currentUser.uid, 'savedNews'),
      (snapshot) => {
        const nextSavedArticles = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          articleCollection: docSnap.data()?.articleCollection || 'newsArticles',
          ...docSnap.data(),
        }));
        setSavedArticles(
          nextSavedArticles.sort((a, b) => {
            const aSaved = a.savedAt?.toDate?.()?.getTime?.() || 0;
            const bSaved = b.savedAt?.toDate?.()?.getTime?.() || 0;
            return bSaved - aSaved;
          })
        );
        setSavedIds(new Set(nextSavedArticles.map((article) => article.id)));
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;

    const unsubscribe = onSnapshot(
      collection(db, 'users', currentUser.uid, 'pinnedNews'),
      (snapshot) => {
        const nextPinnedMap = new Map();
        snapshot.docs.forEach((docSnap) => {
          const pinnedAt = docSnap.data()?.pinnedAt?.toDate?.()?.getTime?.() || 0;
          nextPinnedMap.set(docSnap.id, pinnedAt);
        });
        setPinnedMap(nextPinnedMap);
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      try {
        const tokenResult = await user.getIdTokenResult();
        setIsAdmin(tokenResult.claims?.admin === true);
      } catch (error) {
        console.error('Failed to verify admin claim:', error);
        setIsAdmin(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isAdmin || adminLocalState || !profileState) return;
    setAdminLocalState(profileState);
  }, [adminLocalState, isAdmin, profileState]);

  const nationalArticles = useMemo(() => {
    const articles = mergeStats(feedArticles.national, statsById);
    return sortByPinnedArticles(articles, pinnedMap).slice(0, MAX_VISIBLE_ARTICLES);
  }, [feedArticles.national, pinnedMap, statsById]);

  const localArticles = useMemo(() => {
    if (!canViewSelectedLocalFeed) return [];
    const articles = mergeStats(feedArticles.local_NC, statsById);
    return sortByPinnedArticles(articles, pinnedMap).slice(0, MAX_VISIBLE_ARTICLES);
  }, [canViewSelectedLocalFeed, feedArticles.local_NC, pinnedMap, statsById]);

  const fullVisibleArticles =
    activeTab === 'local'
      ? localArticles
      : activeTab === 'saved'
      ? savedArticles
      : nationalArticles;

  const visibleArticles = useMemo(
    () => fullVisibleArticles.slice(0, visibleCount),
    [fullVisibleArticles, visibleCount]
  );

  const activeStatsCollection =
    activeTab === 'local'
      ? activeLocalFeedKey
        ? FEEDS[activeLocalFeedKey].statsCollection
        : ''
      : activeTab === 'national'
      ? FEEDS.national.statsCollection
      : '';
  const activeArticleIds = activeStatsCollection
    ? visibleArticles.map((article) => article.id)
    : [];
  const activeArticleIdsKey = activeArticleIds.join('|');

  useEffect(() => {
    const ids = activeArticleIdsKey ? activeArticleIdsKey.split('|') : [];
    if (!activeStatsCollection || ids.length === 0) {
      return undefined;
    }

    const unsubscribers = chunkArray(ids, 30).map((chunk) =>
      onSnapshot(
        query(
          collection(db, activeStatsCollection),
          where(documentId(), 'in', chunk)
        ),
        (snapshot) => {
          setStatsById((current) => {
            const next = new Map(current);
            snapshot.docs.forEach((docSnap) => {
              next.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
            });
            return next;
          });
        }
      )
    );

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [activeStatsCollection, activeArticleIdsKey]);

  useEffect(() => {
    if (activeTab === 'saved') return undefined;
    const node = loadMoreRef.current;
    if (!node || visibleCount >= fullVisibleArticles.length) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setVisibleCount((current) =>
          Math.min(current + LOAD_MORE_COUNT, MAX_VISIBLE_ARTICLES)
        );
      }
    }, { rootMargin: '400px' });

    observer.observe(node);
    return () => observer.disconnect();
  }, [activeTab, fullVisibleArticles.length, visibleCount]);

  const handleToggleSave = async (article) => {
    try {
      if (savedIds.has(article.id)) {
        await unsaveNewsArticle(article.id);
      } else {
        await saveNewsArticle(article);
      }
    } catch (error) {
      alert(error?.message || 'Could not update saved articles.');
    }
  };

  const handleTogglePin = async (articleId) => {
    try {
      if (pinnedMap.has(articleId)) {
        await unpinNewsArticle(articleId);
      } else {
        await pinNewsArticle(articleId);
      }
    } catch (error) {
      alert(error?.message || 'Could not update pinned articles.');
    }
  };

  const emptyMessage =
    activeTab === 'saved'
      ? 'No saved articles yet.'
      : activeTab === 'local' && !canViewSelectedLocalFeed
      ? 'Local news is not available for this state yet.'
      : 'No articles available for this cycle yet.';

  return (
    <div className="news-page">
      <div className="news-page-header">
        <div className="news-title-row">
          <h1>News</h1>
          
        </div>
        <div className="news-cycle-pill">{cycleKey}</div>
      </div>

      <div className="news-tabs">
        <button
          type="button"
          className={activeTab === 'national' ? 'active' : ''}
          onClick={() => setActiveTab('national')}
        >
          National
        </button>
        <button
          type="button"
          className={activeTab === 'local' ? 'active' : ''}
          onClick={() => setActiveTab('local')}
        >
          Local
        </button>
        <button
          type="button"
          className={activeTab === 'saved' ? 'active' : ''}
          onClick={() => setActiveTab('saved')}
        >
          Saved
        </button>
      </div>

      {activeTab === 'local' && isAdmin ? (
        <div className="news-admin-local-preview">
          <label htmlFor="adminLocalStateSelect">Admin local feed preview</label>
          <select
            id="adminLocalStateSelect"
            value={selectedLocalState}
            onChange={(event) => {
              setAdminLocalState(event.target.value);
              setVisibleCount(INITIAL_VISIBLE_COUNT);
              setStatsById(new Map());
            }}
          >
            {localStateOptions.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {activeTab === 'local' && !canViewSelectedLocalFeed ? (
        <div className="news-subheader">
          Local news is not available for this state yet.
        </div>
      ) : null}

      {activeTab === 'saved' ? (
        <div className="news-subheader">
          Saved articles expire 2 days after you save them.
        </div>
      ) : null}

      <div className="news-list">
        {visibleArticles.length === 0 ? (
          <div className="news-empty">{emptyMessage}</div>
        ) : (
          visibleArticles.map((article) => (
            <NewsCard
              key={article.id}
              article={article}
              articleCollection={article.articleCollection || 'newsArticles'}
              currentUser={currentUser}
              isSaved={savedIds.has(article.id)}
              isPinned={pinnedMap.has(article.id)}
              isAdmin={isAdmin}
              onToggleSave={handleToggleSave}
              onTogglePin={handleTogglePin}
            />
          ))
        )}
        {activeTab !== 'saved' && visibleCount < fullVisibleArticles.length ? (
          <div ref={loadMoreRef} className="news-load-more-sentinel" />
        ) : null}
      </div>
    </div>
  );
}
